// Agenda del médico — planificador SEMANA / MES con citas coloreadas por tipo.
// Tokens semánticos (opacos en claro y oscuro) + acentos por tipo. Navegable.

import { useEffect, useMemo, useState } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Clock, User2, XCircle, Loader2,
} from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { appointmentsSummary, listAppointments } from '../api';
import { cockpitLabels } from '../labels';
import type { AppointmentOut, AppointmentsSummaryOut } from '../types';

const TYPE_LABEL: Record<string, string> = {
  consulta: 'Consulta', cirugia: 'Cirugía', postoperatorio: 'Postop',
  teleconsulta: 'Teleconsulta', urgencia: 'Urgencia', seguimiento: 'Seguimiento',
};
const TYPE_COLOR: Record<string, { bar: string; bg: string; text: string; dot: string }> = {
  consulta:       { bar: 'bg-sky-500',     bg: 'bg-sky-500/10',     text: 'text-sky-600 dark:text-sky-400',       dot: 'bg-sky-500' },
  cirugia:        { bar: 'bg-rose-500',    bg: 'bg-rose-500/10',    text: 'text-rose-600 dark:text-rose-400',     dot: 'bg-rose-500' },
  postoperatorio: { bar: 'bg-amber-500',   bg: 'bg-amber-500/10',   text: 'text-amber-600 dark:text-amber-400',   dot: 'bg-amber-500' },
  teleconsulta:   { bar: 'bg-violet-500',  bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400', dot: 'bg-violet-500' },
  urgencia:       { bar: 'bg-red-600',     bg: 'bg-red-600/10',     text: 'text-red-600 dark:text-red-400',       dot: 'bg-red-600' },
  seguimiento:    { bar: 'bg-emerald-500', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
};
const DEFAULT_COLOR = { bar: 'bg-primary', bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-primary' };
const colorOf = (t: string) => TYPE_COLOR[t] || DEFAULT_COLOR;

const STATUS_BADGE: Record<string, string> = {
  programada: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  en_curso: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  completada: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  cancelada: 'bg-muted text-muted-foreground line-through',
  no_show: 'bg-destructive/10 text-destructive',
};

const WD = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const WD_MON = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
const startOfWeek = (d: Date) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const dateKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isSameDay = (a: Date, b: Date) => dateKey(a) === dateKey(b);

type View = 'week' | 'month';

export function AgendaView() {
  const { archetype } = useModules();
  const L = cockpitLabels(archetype);
  const [summary, setSummary] = useState<AppointmentsSummaryOut | null>(null);
  const [appointments, setAppointments] = useState<AppointmentOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const today = new Date();

  // Rango visible según la vista.
  const { cells, rangeStart, rangeEnd, title } = useMemo(() => {
    if (view === 'week') {
      const ws = startOfWeek(anchor);
      const cs = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
      return {
        cells: cs, rangeStart: cs[0], rangeEnd: cs[6],
        title: `${cs[0].toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${cs[6].toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      };
    }
    const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(monthStart);
    const cs = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return {
      cells: cs, rangeStart: cs[0], rangeEnd: cs[41],
      title: `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`,
    };
  }, [view, anchor]);

  useEffect(() => { appointmentsSummary().then(setSummary).catch(() => {}); }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true); setError(null);
    listAppointments({ from_date: dateKey(rangeStart), to_date: dateKey(rangeEnd), limit: 500 })
      .then((a) => { if (!cancel) setAppointments(a); })
      .catch((e) => { if (!cancel) setError(String(e?.message || e)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [rangeStart, rangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDay = useMemo(() => {
    const m = new Map<string, AppointmentOut[]>();
    appointments.forEach((a) => {
      const k = dateKey(new Date(a.scheduled_at));
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(a);
    });
    m.forEach((arr) => arr.sort((x, y) => x.scheduled_at.localeCompare(y.scheduled_at)));
    return m;
  }, [appointments]);

  const step = (dir: number) => setAnchor((d) => (view === 'week' ? addDays(d, dir * 7) : addMonths(d, dir)));

  return (
    <div className="space-y-5">
      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard label="Hoy" value={summary.today_count} icon={<Calendar size={16} />} />
          <StatCard label="Mañana" value={summary.tomorrow_count} icon={<Clock size={16} />} />
          <StatCard label="Esta semana" value={summary.week_count} icon={<Calendar size={16} />} />
          <StatCard label={L.metricUniqueShort} value={summary.patients_unique_week} icon={<User2 size={16} />} />
          <StatCard label="No-shows (30d)" value={summary.no_shows_30d} icon={<XCircle size={16} />} tone={summary.no_shows_30d > 0 ? 'warn' : undefined} />
        </div>
      )}

      {/* Nav + toggle vista + leyenda */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setAnchor(new Date())} className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">Hoy</button>
          <div className="flex items-center rounded-lg border border-border bg-card">
            <button onClick={() => step(-1)} className="p-1.5 text-muted-foreground hover:text-foreground"><ChevronLeft size={18} /></button>
            <span className="min-w-[150px] px-2 text-center text-sm font-medium capitalize text-foreground">{title}</span>
            <button onClick={() => step(1)} className="p-1.5 text-muted-foreground hover:text-foreground"><ChevronRight size={18} /></button>
          </div>
          <div className="flex overflow-hidden rounded-lg border border-border">
            {(['week', 'month'] as View[]).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm ${view === v ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}>
                {v === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>
          {loading && <Loader2 size={15} className="animate-spin text-muted-foreground" />}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {Object.entries(TYPE_LABEL).map(([k, label]) => (
            <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${colorOf(k).dot}`} />{label}
            </span>
          ))}
        </div>
      </div>

      {error && <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Error: {error}</div>}

      {view === 'week' ? (
        <WeekGrid cells={cells} byDay={byDay} today={today} />
      ) : (
        <MonthGrid cells={cells} byDay={byDay} today={today} month={anchor.getMonth()} onDay={(d) => { setAnchor(d); setView('week'); }} />
      )}
    </div>
  );
}

function WeekGrid({ cells, byDay, today }: { cells: Date[]; byDay: Map<string, AppointmentOut[]>; today: Date }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <div className="grid min-w-[920px] grid-cols-7 divide-x divide-border">
        {cells.map((day) => {
          const items = byDay.get(dateKey(day)) || [];
          const isToday = isSameDay(day, today);
          return (
            <div key={dateKey(day)} className={isToday ? 'bg-primary/[0.04]' : ''}>
              <div className={`sticky top-0 z-10 flex items-center justify-between border-b border-border px-3 py-2 ${isToday ? 'bg-primary/10' : 'bg-muted/40'}`}>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{WD[day.getDay()]}</div>
                  <div className={`text-lg font-semibold leading-none ${isToday ? 'text-primary' : 'text-foreground'}`}>{day.getDate()}</div>
                </div>
                {items.length > 0 && <span className={`rounded-full px-2 py-0.5 text-[10px] ${isToday ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{items.length}</span>}
              </div>
              <div className="min-h-[420px] space-y-2 p-2">
                {items.length === 0 ? <div className="pt-8 text-center text-[11px] text-muted-foreground/60">—</div>
                  : items.map((a) => <ApptCard key={a.id} a={a} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthGrid({ cells, byDay, today, month, onDay }: {
  cells: Date[]; byDay: Map<string, AppointmentOut[]>; today: Date; month: number; onDay: (d: Date) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {WD_MON.map((w) => <div key={w} className="px-2 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const items = byDay.get(dateKey(day)) || [];
          const isToday = isSameDay(day, today);
          const inMonth = day.getMonth() === month;
          return (
            <button key={dateKey(day)} onClick={() => onDay(day)}
              className={`min-h-[104px] border-b border-r border-border p-1.5 text-left align-top transition hover:bg-muted/40 ${(i + 1) % 7 === 0 ? 'border-r-0' : ''} ${inMonth ? '' : 'bg-muted/20'}`}>
              <div className="mb-1 flex justify-end">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? 'bg-primary font-semibold text-primary-foreground' : inMonth ? 'text-foreground' : 'text-muted-foreground/50'}`}>{day.getDate()}</span>
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((a) => {
                  const c = colorOf(a.type);
                  return (
                    <div key={a.id} className={`flex items-center gap-1 truncate rounded px-1 py-0.5 text-[10px] ${c.bg}`}>
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.dot}`} />
                      <span className="font-medium text-foreground">{fmtTime(a.scheduled_at)}</span>
                      <span className="truncate text-muted-foreground">{a.patient_name}</span>
                    </div>
                  );
                })}
                {items.length > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{items.length - 3} más</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApptCard({ a }: { a: AppointmentOut }) {
  const c = colorOf(a.type);
  const cancelled = a.status === 'cancelada' || a.status === 'no_show';
  return (
    <div className={`relative overflow-hidden rounded-lg border border-border ${c.bg} p-2 pl-3 transition hover:shadow-sm ${cancelled ? 'opacity-60' : ''}`}>
      <span className={`absolute left-0 top-0 h-full w-1.5 ${c.bar}`} />
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-semibold text-foreground">{fmtTime(a.scheduled_at)}</span>
        <span className={`text-[10px] font-medium uppercase ${c.text}`}>{TYPE_LABEL[a.type] || a.type}</span>
      </div>
      <div className={`mt-0.5 truncate text-sm font-medium text-foreground ${cancelled ? 'line-through' : ''}`}>
        {a.patient_name}{a.patient_age !== null && <span className="font-normal text-muted-foreground"> · {a.patient_age}a</span>}
      </div>
      {a.motivo && <div className="truncate text-[11px] text-muted-foreground">{a.motivo}</div>}
      <div className="mt-1 flex items-center gap-1">
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${STATUS_BADGE[a.status] || 'bg-muted text-muted-foreground'}`}>{a.status}</span>
        {a.is_demo && <span className="rounded bg-amber-500/15 px-1 py-0.5 text-[9px] uppercase text-amber-600 dark:text-amber-400">demo</span>}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, tone }: { label: string; value: number; icon: React.ReactNode; tone?: 'warn' }) {
  const toneClass = tone === 'warn' ? 'text-amber-500' : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{label}</span><span className={toneClass}>{icon}</span></div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}
